import { Injectable } from '@angular/core';
import type {
  CatalogNodeDefinition,
  NodeCatalogDocument,
  NodePorts,
} from '../models/node-catalog.types';
import catalogJson from './node-catalog.json';

const OTP_NODE_TYPES = new Set(['validation.phone-validation']);
const FORM_ENTRY_NODE_TYPES = new Set(['forms.render-form', 'forms.load-form']);
const DATASET_NODE_TYPES = new Set(['data-display.dataset']);
const RECORD_DETAIL_NODE_TYPES = new Set(['data-display.record-detail']);
const SCREEN_TEMPLATE_NODE_TYPES = new Set(['ui-screen.render-template']);
const CHOICE_NODE_TYPES = new Set([
  'applicant.select-applicant-type',
  'applicant.select-customer-status',
]);
const DOCUMENT_UPLOAD_NODE_TYPES = new Set([
  'documents.upload-document',
  'documents.scan-document',
]);
const START_NODE_TYPES = new Set(['START', 'FLOW-CONTROL.START']);
const END_NODE_TYPES = new Set([
  'END',
  'FLOW-CONTROL.END',
  'FLOW-CONTROL.TERMINATE',
  'FLOW-CONTROL.SUCCESS',
  'FLOW-CONTROL.FAILURE',
]);

@Injectable({ providedIn: 'root' })
export class NodeCapabilityRegistry {
  private readonly nodesById = new Map<string, CatalogNodeDefinition>();

  constructor() {
    const catalog = catalogJson as NodeCatalogDocument;
    for (const node of catalog.nodes) {
      this.nodesById.set(node.id.toLowerCase(), node);
    }
  }

  get(nodeType: string): CatalogNodeDefinition | undefined {
    return this.nodesById.get(nodeType.toLowerCase());
  }

  getAll(): CatalogNodeDefinition[] {
    return [...this.nodesById.values()];
  }

  resolveCategory(nodeType: string): string {
    const fromCatalog = this.get(nodeType)?.category;
    if (fromCatalog) return String(fromCatalog).toLowerCase();
    const dotIndex = nodeType.indexOf('.');
    const raw = dotIndex === -1 ? nodeType : nodeType.slice(0, dotIndex);
    return raw.toLowerCase();
  }

  getPorts(nodeType: string): NodePorts {
    return (
      this.get(nodeType)?.ports ?? {
        inputs: [{ id: 'in', label: 'In' }],
        outputs: [{ id: 'out', label: 'Out' }],
      }
    );
  }

  isStartNode(nodeType: string): boolean {
    return START_NODE_TYPES.has(nodeType.toUpperCase());
  }

  isEndNode(nodeType: string): boolean {
    return END_NODE_TYPES.has(nodeType.toUpperCase());
  }

  isOtpNode(nodeType: string): boolean {
    return OTP_NODE_TYPES.has(nodeType.toLowerCase());
  }

  isFormEntryNode(nodeType: string): boolean {
    return FORM_ENTRY_NODE_TYPES.has(nodeType.toLowerCase());
  }

  isDatasetNode(nodeType: string): boolean {
    return DATASET_NODE_TYPES.has(nodeType.toLowerCase());
  }

  isRecordDetailNode(nodeType: string): boolean {
    return RECORD_DETAIL_NODE_TYPES.has(nodeType.toLowerCase());
  }

  isScreenTemplateNode(nodeType: string): boolean {
    return SCREEN_TEMPLATE_NODE_TYPES.has(nodeType.toLowerCase());
  }

  isChoiceNode(nodeType: string): boolean {
    const type = nodeType.toLowerCase();
    return CHOICE_NODE_TYPES.has(type) || type.includes('choice-node');
  }

  isDocumentUploadNode(nodeType: string): boolean {
    return DOCUMENT_UPLOAD_NODE_TYPES.has(nodeType.toLowerCase());
  }

  isApprovalCategory(nodeType: string): boolean {
    const category = this.resolveCategory(nodeType);
    return category === 'approval' || category === 'human-interaction';
  }

  isTaskPauseNode(nodeType: string): boolean {
    const category = this.resolveCategory(nodeType);
    if (category === 'tasks') {
      const type = nodeType.toLowerCase();
      return (
        type.includes('create-task') ||
        type.includes('assign-task') ||
        type.includes('checklist') ||
        type.includes('comment') ||
        type.includes('audit-note')
      );
    }
    return nodeType.toLowerCase() === 'flow-control.manual-task';
  }

  /**
   * True when the walker must pause and wait for UI / external input.
   * Driven by catalog capabilities plus known special cases (OTP, form entry, dataset).
   */
  isPauseNode(nodeType: string): boolean {
    const def = this.get(nodeType);
    if (def?.capabilities.humanInteraction) return true;
    return (
      this.isChoiceNode(nodeType) ||
      this.isOtpNode(nodeType) ||
      this.isFormEntryNode(nodeType) ||
      this.isDatasetNode(nodeType) ||
      this.isRecordDetailNode(nodeType) ||
      this.isScreenTemplateNode(nodeType) ||
      this.isDocumentUploadNode(nodeType)
    );
  }
}
